<template>
  <div class="landing-page-v2 jumpstart-2025" :class="[$config.platform, { extend: promotion.extend }]">
    <Hero
      :stats="stats" :assets="assets.hero" :cols="{ left: 'col-6', right: 'col-6' }"
      :benefits="benefits"
    >
      <template slot="title">
        <h1 class="main">
          <span class="highlight white special">JUMPSTART</span> YOUR
          <span class="block"><span class="highlight blue special">2025 {{ $config.platform === 'ds' ? 'DATA & AI' : 'FINANCE' }}</span> CAREER</span>
        </h1>
      </template>
      <div slot="content" class="card">
        <p class="label">
          Limited-Time Offer
        </p>
        <p class="old-price">
          <span class="del">&nbsp;{{ page.prices.basePrice | formatCurrency(prices.currency, { minimumFractionDigits: 2 }) }}&nbsp;</span>
        </p>
        <p class="new-price">
          <span class="discounted-price">{{ priceSplitted[0] | formatCurrency(prices.currency, { minimumFractionDigits: 0 }) }}</span>
          <span v-if="priceSplitted[1]" class="medium">.{{ priceSplitted[1] }}</span>
          <span class="small">/year</span>
        </p>
        <p class="text">
          Billed Annually
        </p>
        <nuxt-link :to="`/checkout/?plan=annual&coupon=${promotion.coupon}`" class="btn-primary campaign-1 submit">
          Buy Now
        </nuxt-link>
        <p class="card-text">
          Price-lock guarantee
        </p>
        <p class="card-text">
          30-day money-back guarantee
        </p>
        <div v-if="assets.hero.stamp" class="stamp not-image">
          {{ promotion.discount }}%<br>OFF
        </div>
      </div>
      <CounterWithDiscount
        v-if="countdownEndDate"
        slot="countdown"
        :key="promotion.discount + 1 + countdownEndDate.getTime()"
        :end-date="countdownEndDate"
        counter-class="filled discount-extended desktop-only"
        :discount-on-left="promotion.counterDiscountOnLeft"
        :discount-on-right="promotion.counterDiscountOnRight"
        :hide-days="promotion.counterHideDays"
        :discount="promotion.discount"
        :discount-text="countdownCurrentDiscount"
      />
      <CounterWithDiscount
        v-if="countdownEndDate"
        slot="countdown-mobile"
        :key="promotion.discount + 1 + countdownEndDate.getTime()"
        :end-date="countdownEndDate"
        counter-class="filled discount-extended mobile-only"
        :discount-on-left="promotion.counterDiscountOnLeft"
        :discount-on-right="promotion.counterDiscountOnRight"
        :hide-days="promotion.counterHideDays"
        :discount="promotion.discount"
        :discount-text="countdownCurrentDiscount"
      />
      <div slot="footer-text">
        <div>
          Grab your Annual Plan discount before Jan 30.
        </div>
        <div class="upper">
          <span class="mobile-block">BUT HURRY!</span> <span class="highlight campaign-3">PRICE GOES UP</span> EVERY 48H!
        </div>
      </div>
    </Hero>
    <div class="container companies-footer">
      <Companies
        v-if="companiesSmall && companiesSmall.length"
        :companies="companiesSmall" title="Trusted by employees from:" no-fill
        is-campaign
      />
      <div class="trustpilot-review">
        <IconTrustpilot />
        <span class="rating">{{ $config.platform === 'ds' ? 4.8 : 4.7 }}/5</span>
        <StarsRatingCampaign />
      </div>
    </div>
    <div v-if="$config.platform === 'ds'" class="section graduations">
      <Videos :videos="videos" :video-js-loaded="videoJsLoaded">
        <template slot="title">
          <span class="section-title">
            What Do Our <span class="highlight blue">GRADUATES</span> Say?
          </span>
        </template>
      </Videos>
    </div>
    <section class="section free">
      <div class="container">
        <h2 class="section-title">
          What Will You <span class="highlight campaign-3 fa-campaign-1">GET</span>?
        </h2>
        <div v-if="$config.platform === 'ds'" class="section-description muted">
          With online courses, practical exercises, exams, and real-world projects, you'll build essential skills and <strong class="highlight yellow">earn certifications</strong> that set you apart in the job market. At 365 Data Science, we understand you value flexibility and practicality in your learning. That's why we provide comprehensive training programs that teach you essential skills through real-life case studies—all at your own pace.
        </div>
        <div v-else class="section-description muted">
          With online courses, practical exercises, exams, and real-world projects, you'll build essential skills and <strong class="highlight yellow">earn certifications</strong> to help you stand out in the competitive job market. 365 Financial Analyst empowers today’s busy learners to excel in their finance careers with comprehensive, <strong>cutting-edge training programs</strong>.
        </div>
        <client-only>
          <div class="cards row">
            <div
              v-for="(card, idx) in benefitCards" :key="idx"
              slot="slide"
              class="card col col-4 sm-6"
              :class="`id-${idx}`"
            >
              <div class="image-wrapper">
                <img :src="require(`../assets/images/campaigns/${$config.platform}-${card.image}.webp`)" class="card-image">
              </div>
              <h4 class="card-title" v-html="card.title" />
              <span class="card-desc muted" v-html="card.text" />
            </div>
          </div>
        </client-only>
      </div>
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          :data-event="`${$route.path.replace(/\//g, '')}_v1_free.click`"
          @click="subscribe"
        >
          Start at {{ promotion.discount }}% OFF
        </button>
      </div>
    </section>
    <div class="section instructors">
      <div class="instructors-slider">
        <div class="container heading">
          <h2 class="section-title" v-html="instructorsTitle" />
          <Companies
            v-if="$config.platform === 'fa' && companiesFa && companiesFa.length" :companies="companiesFa"
            no-fill title=""
          />
        </div>
        <SliderInfinite
          :key="`instr${$store.state.layout}`"
          :offset="0" heading="h2" heading-class="section-title"
        >
          <div
            v-for="(instructor, idx) in instructors"
            :key="`${idx}${$store.state.layout}`"
            slot="slide"
          >
            <component :is="$config.platform === 'ds' ? 'nuxt-link' : 'span'" :to="`/instructors/${instructor.slug}`">
              <img :src="require(`../assets/images/campaigns/${$config.platform}-js2025-instructor-${instructor.image || instructor.slug}${$store.state.layout === 'mobile' ? '-mobile' : ''}.webp`)" :alt="instructor.name">
            </component>
          </div>
        </SliderInfinite>
      </div>
    </div>
    <div class="section courses-extended" :class="$config.platform">
      <div class="head container">
        <h2 class="section-title">
          Prepare for Your Dream Career with <span class="highlight campaign-3 fa-campaign-1">EXPERT-LED {{ $config.platform === 'fa' ? ' COURSES' : '' }}</span>{{ $config.platform === 'ds' ? ' Courses' : '' }}
        </h2>
        <h3 class="muted">
          What’s your ideal job?
        </h3>
        <ul>
          <li
            v-for="(g, idx) in cGroups" :key="idx" :class="{ active: idx === courseGroupIdx }"
            @click="switchCourseGroup(idx)"
          >
            {{ g.name }}
          </li>
        </ul>
        <p class="description">
          Become a certified
          <span class="highlight yellow thin">{{ cGroup.description.title }}<span class="mobile-only">,</span></span><span class="desktop-only">,</span> earning
          <span class="static-salary-container">
            <span class="animation-salary" :class="{ animated: salaryAnimation.animatedOnScroll }">
              <span
                class="holder animation" :class="{ active: salaryAnimation.activate }"
                :style="`--width: ${salaryAnimation.width || 188}px`"
                v-html="salary"
              />
            </span>
          </span>
        </p>
      </div>
      <Courses
        :key="`c-slider${courseGroupIdx}`" :courses="cGroupCourses" :offset="-2"
        heading="h3"
      />
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          :data-event="`${$route.path.replace(/\//g, '')}_v1_courses.click`"
          @click="subscribe"
        >
          Save {{ promotion.discount }}% Now
        </button>
      </div>
    </div>
    <div class="section stats" :class="$config.platform">
      <StatsIcons
        class="container"
        :stats="stats" colored auto-width
        :order="['students', 'courses', 'projects']"
      />
    </div>
    <section class="section long-reviews-holder">
      <Reviews
        :title="reviewsTitle" title-class="title-reset title-highlight"
        class="long-reviews" dark unique-class="second"
      />
    </section>
    <section class="section limited-offer-holder">
      <LimitedOffer
        :options="limitedOfferOptions"
        :features="footerFeatures"
        campaign-page class-name="col-8"
        @subscribe="$router.push(`/checkout/?plan=annual&coupon=${promotion.coupon}`)"
      >
        <template slot="pre-title">
          Price Goes Up Every 48H Until Jan 30!
        </template>
      </LimitedOffer>
    </section>
    <client-only>
      <BottomBanner
        v-if="$store.state.layout === 'desktop'"
        :key="1 + countdownEndDate.getTime()"
        :class="$config.platform"
        :options="bottomBannerOptions"
        :discount="promotion.discount"
        :closed="bannerClosed"
        @subscribe="subscribe"
        @close="closeBanner"
      >
        <template slot="heading">
          <div>
            <span class="block highlight white special">JUMPSTART 2025</span>
            <span class="block">
              WITH <span class="highlight blue fa-campaign-3 special-ds">{{ $config.platform === 'ds' ? 'DATA & AI' : 'FINANCE' }} COURSES</span>
            </span>
          </div>
        </template>
        <CounterWithDiscount
          slot="countdown"
          :key="promotion.discount"
          :end-date="countdownEndDate"
          counter-class="filled discount-extended second"
          :discount-on-left="promotion.counterDiscountOnLeft"
          :discount-on-right="promotion.counterDiscountOnRight"
          :hide-days="promotion.counterHideDays"
          :discount="promotion.discount"
          :discount-text="countdownCurrentDiscount"
        />
      </BottomBanner>
    </client-only>
  </div>
</template>

<script>
import { CAMPAIGN_PROMOTIONS, CAMPAIGN_INSTRUCTORS } from '@itbpbg/lms-components/src/utils/campaigns';
import { getPromotion, dateInRightInterval, loadHotJar } from '@itbpbg/lms-components/src/utils/helper';
import global from '@itbpbg/lms-components/src/mixins/global';
import Companies from '@itbpbg/lms-components/src/Campaigns/Companies';
import SliderInfinite from '@itbpbg/lms-components/src/components/SliderInfinite';
import Videos from '@itbpbg/lms-components/src/Campaigns/Videos';
import CounterWithDiscount from '@itbpbg/lms-components/src/CounterWithDiscount';
import StarsRatingCampaign from '@itbpbg/lms-components/src/assets/svg/campaign-stars-rating.svg?vue-component';
import Rating from '@itbpbg/lms-components/src/Rating';
import Hero from '../components/Campaigns/Hero';
import Courses from '../components/Campaigns/Courses';
import Video from '../components/Campaigns/Video';
import HomeSectionInfinite from '../components/Home/HomeSectionInfinite';
import StatsIcons from '../components/Campaigns/StatsIcons';
import Reviews from '../components/Landing/Reviews';
import LimitedOffer from '../components/Landing/LimitedOffer';
import BottomBanner from '../components/Landing/BottomBanner';
import IconTrustpilot from '../assets/svg/icon-trustpilot.svg?vue-component';

export default {
  components: {
    Hero,
    Companies,
    SliderInfinite,
    Courses,
    Video,
    HomeSectionInfinite,
    IconTrustpilot,
    StarsRatingCampaign,
    Rating,
    Videos,
    Reviews,
    StatsIcons,
    CounterWithDiscount,
    LimitedOffer,
    BottomBanner,
  },
  mixins: [global],
  async asyncData({
    $axios,
    $config,
    route,
    error,
  }) {
    const {
      page, courses, stats, projects, coursesGroups,
    } = await $axios.$get(`/pages/campaign/jumpstart-2025?prices=1&courses=1&coursesExtended=1&projects=1${$config.platform === 'fa' ? '&projectsExtended=1' : ''}`)
      .catch(() => error({ statusCode: 404, message: 'Page not found' }));

    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.created = page.created || new Date();
    page.updated = page.updated || new Date();

    return {
      courses, stats, page, projects, coursesGroups,
    };
  },
  data() {
    return {
      assets: {
        hero: {
          stamp: true,
          background: `${this.$config.platform}-js2025-bg-hero.webp`,
          backgroundMobile: `${this.$config.platform}-js2025-bg-hero-mobile.webp`,
        },
        footer: {
          background: `${this.$config.platform}-js2025-bg-footer.webp`,
          backgroundMobile: `${this.$config.platform}-js2025-bg-footer-mobile.webp`,
        },
        backgroundsAsImages: false,
      },
      videoIds: {
        ds: '6365035622112',
        fa: '6365036305112',
      },
      videoJsLoaded: false,
      videoAlreadyPlayed: false,
      videoTimeout: null,
      bannerClosed: false,
      countdownEndDate: this.saleEndDate(),
      promotion: {},
      instructorsTitle: (this.$config.platform === 'ds'
        ? 'Learn Data Science with <span class="highlight blue">INSTRUCTORS</span> from<br><span class="smaller">Meta, Netflix, Google, Spotify, IKEA, etc.</span>'
        : 'Learn Finance from <strong class="highlight blue">EXPERTS</strong> at High-Profile Companies'),
      benefitCards: [
        {
          title: this.$config.platform === 'ds' ? 'Certificates' : 'Finance Certificates',
          text: (this.$config.platform === 'ds' ? 'Demonstrate your data and AI expertise by obtaining certifications that validate your skills.' : 'Demonstrate your finance expertise by earning finance certificates as a <strong>tangible proof</strong> of your skills.'),
          image: 'js2025-certificates',
        },
        {
          title: 'Personalized Learning Plan',
          text: 'Build the ideal learning journey for your desired job and <strong>strengthen your abilities</strong> with a personalized skill plan.',
          image: 'js2025-clp',
        },
        {
          title: 'Career-Focused Training',
          text: (this.$config.platform === 'ds' ? 'Embark on the data and <strong>AI career path of your dreams<strong> with a collection of courses to obtain role-specific skills.' : 'Embark on the finance <strong>career path of your dreams</strong> with a collection of courses to obtain role-specific skills.'),
          image: 'js2025-tracks',
        },
        {
          title: 'Practice Tests',
          text: (this.$config.platform === 'ds' ? 'Test your newfound data and AI skills with our practical exams. Each <strong>course and career-focused training</strong> offers a verifiable certificate.' : 'Test your newfound finance skills with our practical exams. Each course and <strong>career-focused training</strong> offers a verifiable certificate.'),
          image: 'js2025-exams',
        },
        {
          title: this.$config.platform === 'ds' ? 'Data Projects' : 'Finance Projects',
          text: (this.$config.platform === 'ds' ? 'Boost your portfolio with <strong>real-world data and ML projects</strong>. Learn by doing with business cases you\'ll encounter on the job.' : 'Boost your resume with <strong>real-world finance projects</strong> based on actionable use cases that provide valuable business insights.'),
          image: 'js2025-projects',
        },
        {
          title: 'Interactive Exercises',
          text: (this.$config.platform === 'ds' ? 'Apply your knowledge with coding tasks, fill-in-the-blank exercises, true or false questions, <strong>flashcards</strong>, and more.' : 'Apply your knowledge with fill-in-the-blank exercises, true or false questions, <strong>flashcards</strong>, and more.'),
          image: 'js2025-exercises',
        },
      ],
      videos: [
        {
          name: 'Lee McFarling',
          image: 'lee',
          from: 'USA',
          id: 6315230915112,
          quote: 'After 6 months of learning with 365 Data Science, I was getting job interviews as a business analyst or a data analyst.',
          job: 'Data Analyst, Astreya',
        },
        {
          name: 'Meghan Verschoor',
          image: 'meghan',
          from: 'Canada',
          id: 6315228912112,
          quote: 'I find the platform really easy to follow through with and learn the skills that I needed to apply to my job. I highly recommend it.',
          job: 'Director, Data Science & Management, Turnstone Biologics',
        },
        {
          name: 'Nada Abdulwahab',
          image: 'nada',
          from: 'Egypt',
          id: 6315228795112,
          quote: '365 gave me the basics to start my career […] and still helps me enhance my skills and push my career to a higher level.',
          job: 'Senior Data Engineer, VOIS',
        },
        {
          name: 'Julián Elizondo',
          image: 'julián',
          from: 'Mexico',
          id: 6343656185112,
          quote: 'If it wasn’t for 365 Data Science and the confidence it gave me, I wouldn’t have landed my current job.',
          job: 'Tableau Developer, Schneider Electric',
        },
        {
          name: 'Mihaela Paparinova',
          image: 'mihaela',
          from: 'Bulgaria',
          id: 6341101204112,
          quote: 'The program helped me excel in my studies and deepen my understanding of niche data science concepts.',
          job: 'Data Science Student, Sofia University',
        },
        {
          name: 'Ernest Asena',
          image: 'ernest',
          from: 'Kenya',
          id: 6315231920112,
          quote: 'Eight months into the program, I started applying for entry-level positions, and I landed a job as a junior data scientist.”',
          job: 'Data Insights Analytics Cloud Engineer, AWS',
        },
        {
          name: 'Ahmed Eldessouky',
          image: 'ahmed',
          from: 'Egypt',
          id: 6327293863112,
          quote: 'I was impressed with the quality of the video lessons, and the topics and different tools the program covers.',
          job: 'Business Developer, Squadio',
        },
      ],
      heroAnimation: {
        strings: [
          'data scientist',
          'data analyst',
          'business analyst',
          'ML engineer',
          'AI engineer',
        ],
        index: 0,
        animate: false,
      },
      salaryAnimation: {
        activate: false,
        animatedOnScroll: false,
      },
      technologies: [
        { icon: 'python', text: 'Python' },
        { icon: 'git', text: 'Git and Github' },
        { icon: 'tableau', text: 'Tableau' },
        { icon: 'r' },
        { icon: 'excel', text: 'Excel' },
        { icon: 'jupyter', text: 'Jupyter' },
        { icon: 'sql' },
        { icon: 'chatgpt', text: 'ChatGPT' },
        { icon: 'powerbi', text: 'Power BI' },
      ],
      companies: [
        ...(this.$config.platform === 'ds' ? [
          { icon: 'meta-new' },
          { icon: 'netflix' },
          { icon: 'google-new' },
          { icon: 'spotify' },
          { icon: 'ikea' },
          { icon: 'coca-cola-auto' },
          { icon: 'bbc' },
          { icon: 'pwc-new' },
        ] : [
          { icon: 'hsbc-new' },
          { icon: 'morgan-stanley-new' },
          { icon: 'goldman-sachs-new' },
          { icon: 'pwc-new' },
          { icon: 'coca-cola-auto' },
          { icon: 'ikea-new' },
        ]),
      ],
      companiesFa: [
        { icon: 'hsbc-new' },
        { icon: 'morgan-stanley-new' },
        { icon: 'goldman-sachs-new' },
        { icon: 'pwc-new' },
        { icon: 'coca-cola-auto' },
        { icon: 'ikea-new' },
      ],
      companiesSmall: [
        ...(this.$config.platform === 'ds' ? [
          { icon: 'oracle' },
          { icon: 'amazon' },
          { icon: 'walmart' },
          { icon: 'alphabet' },
          { icon: 'meta' },
        ] : [
          { icon: 'hsbc' },
          { icon: 'morgan-stanley' },
          { icon: 'goldman-sachs' },
          { icon: 'pwc' },
          { icon: 'coca-cola' },
          { icon: 'ikea' },
        ]),
      ],
      instructors: CAMPAIGN_INSTRUCTORS[this.$config.platform],
      courseGroupIdx: 0,
      reviewsTitle: `Join a Vibrant Community of <strong class="highlight blue">${this.$c.text('students.count')}+ Students</strong>`,
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    // const brightcoveSrc = [
    //   { src: 'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js' },
    //   { src: 'https://players.brightcove.net/videojs-quality-menu/1/videojs-quality-menu.min.js' },
    // ];

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps({ ...this.page, metaImage: this.page.ogImage || `og-campaign-${this.$config.platform}-jumpstart-2025.webp` }),

      script: [
        // ...brightcoveSrc,
        linkedData,
        {
          src:
            'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js',
          async: true,
        },
      ],
    };
  },
  computed: {
    countdownCurrentDiscount() {
      if (this.$store.state.layout === 'mobile') {
        return 'Discount';
      }

      return 'Current Discount';
    },
    projectsDescription() {
      if (this.$config.platform === 'fa') {
        return 'Bridge the gap between theoretical knowledge and practical implementation by working on real-world finance projects and gather valuable experience for your portfolio. Start with beginner-friendly projects or explore advanced topics to challenge yourself.';
      }

      return 'Bridge the gap between theoretical knowledge and practical implementation by working on real-world data projects—gathering valuable experience for your portfolio. Start with beginner-friendly projects or explore advanced topics to challenge yourself.';
    },
    cGroups() {
      if (this.$config.platform === 'fa') {
        return this.coursesGroups;
      }

      const mapped = [
        'AI Engineer',
        'Machine Learning Engineer',
        'Data Analyst',
        'Data Scientist',
        'Business Intelligence Analyst',
        'Data Engineer',
        'Tableau Developer',
        'Power BI Developer',
        'Business Analyst',
      ];

      return mapped.map((t) => this.coursesGroups.find((g) => g.name === t) || {});
    },
    cGroup() {
      return this.cGroups[this.courseGroupIdx] || {};
    },
    cGroupCourses() {
      return this.cGroup.courses.map((id) => {
        if (typeof id === 'object') {
          return id;
        }

        return this.courses.find((c) => c.id === id) || {};
      }).filter((v) => Object.keys(v).length);
    },
    benefits() {
      if (this.$config.platform === 'fa') {
        return [
          `${this.stats.rounded.courses}+ expert-led courses on Excel, financial analysis, modeling, valuation, accounting, corporate finance, investment banking, and more`,
          'Industry-recognized <strong>certificates</strong>',
          'Real-world finance <strong>projects</strong>',
          'Career-focused <strong>training</strong>',
        ];
      }

      return [
        `<strong>${this.stats.rounded.courses}+ expert-led courses</strong> on Python, R, SQL, ChatGPT, Excel, machine learning, AI, and more`,
        'Industry-recognized <strong>certificates</strong>',
        'Real-world data <strong>projects</strong>',
        'Structured <strong>career tracks</strong>',
      ];
    },
    salary() {
      const [salary, year] = this.cGroup.description.earn.split('/');
      const salaryBolded = `<span class="letter bold">${salary.split('').join('</span><span class="letter bold">')}</span>`;
      const yearNormal = `<span class="letter">${year.split('').join('</span><span class="letter">')}</span>`;

      return `${salaryBolded}<span class="letter">/</span>${yearNormal}`;
    },
    limitedOfferOptions() {
      const price = this.page && this.page.prices.price ? this.page.prices.price : 119;
      const baseAnnualPrice = this.page && this.page.prices.basePriceComparison ? this.page.prices.basePriceComparison : 432;

      return {
        preTitleSlot: true,
        htmltitle: `The Best Place to Jumpstart Your<br><span class="highlight blue">${this.$config.platform === 'ds' ? 'DATA & AI' : 'FINANCE'} CAREER</span> in 2025`,
        promoPlanType: 'annual',
        comparisonPlanType: 'monthly',
        comparisonPlanBilledText: 'Billed Monthly',
        promoPlanBilledText: 'Billed Annually',
        regularPrice: this.$options.filters.formatCurrency(this.page.prices.basePrice, this.prices.currency, { minimumFractionDigits: 0 }),
        discountedPrice: this.$options.filters.formatCurrency(price, this.prices.currency, { minimumFractionDigits: 0 }),
        billedComparisonPrice: this.$options.filters.formatCurrency(baseAnnualPrice, this.prices.currency, { minimumFractionDigits: 0 }),
        discountedPriceCharge: `/y${this.$store.state.layout === 'mobile' ? '' : 'ear'}`,
        billedComparisonCharge: '/month',
        promoPlanSubscribeClass: 'subscribe-annual',
        comparisonSubscribeClass: 'subscribe-monthly',
        promoPlanButtonText: `Start Now at ${this.promotion.discount}% Off`,
        buttonClass: '',
        comparisonPlanButtonText: 'Sign up Now',
        vat: 'VAT may apply',
        badges: true,
        badgesNeutral: true,
      };
    },
    bottomBannerOptions() {
      return {
        counterWithDiscount: true,
        subscribeClass: 'submit',
        buttonText: 'Buy Now',
        className: this.promotion.final ? 'final-promo' : '',
        futureProof: true,
      };
    },
    footerFeatures() {
      const data = [
        { name: 'World-class instructors' },
        { name: `Real-world ${this.$config.platform === 'ds' ? 'data' : 'finance'} projects` },
        { name: 'Expert-led courses' },
        { name: 'Certificates of achievement' },
        { name: 'Self-paced online lessons' },
        { name: 'Resume builder' },
        { name: 'Structured career tracks' },
        { name: 'Community forum' },
        { name: 'Interactive exercises' },
        { name: '30-day money-back guarantee' },
        { name: 'Hands-on exams' },
        { name: 'Price-lock guarantee' },
      ];

      if (this.$store.state.layout === 'mobile') {
        return [
          ...data.filter((v, i) => i % 2 === 0),
          ...data.filter((v, i) => i % 2 === 1),
        ];
      }

      return data;
    },
    prices() {
      return this.page.prices;
    },
    priceSplitted() {
      return (+this.prices.price || 0).toString().split('.');
    },
  },
  created() {
    this.countdownEndDate = this.saleEndDate();
  },
  async mounted() {
    loadHotJar();

    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
    // Hero animation
    setInterval(() => {
      this.heroAnimation.animate = true;
      setTimeout(() => {
        this.heroAnimation.index = (this.heroAnimation.index + 1) % this.heroAnimation.strings.length;
      }, 300);

      setTimeout(() => {
        this.heroAnimation.animate = false;
      }, 600);
    }, 1200);

    this.$nextTick(() => this.toggleBanner());
    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
    this.handleScroll();

    let stop = false;
    const interval = setInterval(() => {
      if (stop) {
        clearInterval(interval);
      }

      const nowDate = new Date();
      if (dateInRightInterval(nowDate)) {
        this.countdownEndDate = this.saleEndDate();
      }

      if (nowDate > new Date(this.promotion.endDateFinal)) {
        stop = true;
        window.location.href = `${this.$config.siteUrl}/offer-expired/`;
      }
    }, 1000);

    window.addEventListener('scroll', this.handleScroll);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.handleScroll));
  },
  methods: {
    iconLogo(icon) {
      return icon ? require(`@itbpbg/lms-components/src/assets/svg/companies/logo-${icon}.svg?vue-component`) : '';
    },
    handleScroll() {
      if (process.client) {
        const salary = this.$el.querySelector('.animation-salary');
        if (salary && !this.salaryAnimation.animatedOnScroll) {
          const salaryBounds = salary.getBoundingClientRect();
          if (salaryBounds.top < window.innerHeight * 0.7) {
            this.switchCourseGroup(0, true);
            this.salaryAnimation.animatedOnScroll = true;
          }
        }

        const video = this.$el.querySelector('.video-container');
        if (video) {
          const videoBounds = video.getBoundingClientRect();
          if (videoBounds.top < window.innerHeight * 0.7 && videoBounds.top > window.innerHeight * -1 && this.$store.state.layout !== 'mobile' && !this.videoAlreadyPlayed) {
            clearTimeout(this.videoTimeout);
            this.videoTimeout = setTimeout(() => {
              this.$root.$emit('video-play', this.videoIds[this.$config.platform]);
              this.videoAlreadyPlayed = true;
            }, 200);
          }
        }

        if (this.$store.state.layout !== 'mobile' && !this.bannerClosed) {
          this.$nextTick(() => this.toggleBanner());
        }
      }
    },
    icon(icon) {
      const name = icon === 'sql' ? icon : `tech-responsive-${icon}`;
      return icon ? require(`@itbpbg/lms-components/src/assets/svg/${name}.svg?vue-component`) : '';
    },
    switchCourseGroup(idx, force = false) {
      if (idx === this.courseGroupIdx && !force) {
        return;
      }

      if (this.salaryAnimation.activate) {
        this.salaryAnimation.activate = false;
      }

      let startAnimationAfter = 10;

      if (!force && this.$store.state.layout === 'mobile') {
        const coursesSlider = this.$el.querySelector('.courses-extended .infinite-carousel');
        if (coursesSlider) {
          startAnimationAfter = 500;
          coursesSlider.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }

      this.salaryAnimation.animatedOnScroll = true;

      setTimeout(() => {
        this.courseGroupIdx = idx;
        this.$nextTick(() => {
          let width = 0;
          const holder = this.$el.querySelector('.animation-salary .holder');
          const letters = holder.querySelectorAll('.letter');
          for (let idx2 = 0; idx2 < letters.length; idx2 += 1) {
            const letter = letters[idx2];
            width += letter.offsetWidth;
          }
          this.salaryAnimation.width = width;
          this.salaryAnimation.activate = true;
          setTimeout(() => {
            for (let idx3 = 0; idx3 < letters.length; idx3 += 1) {
              const letter = letters[idx3];
              setTimeout(() => {
                letter.classList.add('show');
              }, idx3 * 100);
            }
          }, 200);
        });
      }, startAnimationAfter);
    },
    subscribe() {
      const plan = this.$store.getters['subscriptions/annual'] || {};
      this.$store.dispatch('dataLayer/addEvent', { event: 'addToCart', value: { ...plan, planTitle: plan.title, currency: plan.currency.value } });

      return this.$router.push({
        path: '/checkout/',
        query: { plan: 'annual', coupon: this.promotion.coupon },
      });
    },
    toggleBanner() {
      const bottomBanner = document.querySelector('.bottom-banner');
      const from = document.querySelector('.companies-footer');
      const to = document.querySelector('.limited-offer-holder');
      let hideFromTop = false;
      let hideFromBottom = false;

      if (bottomBanner) {
        if (from) {
          hideFromTop = from.getBoundingClientRect().top - 100 > 0;
        }

        if (to) {
          hideFromBottom = to.getBoundingClientRect().top - 400 < 0;
        }

        if (hideFromTop || hideFromBottom) {
          bottomBanner.classList.remove('fixed');
        } else {
          bottomBanner.classList.add('fixed');
        }
      }
    },
    closeBanner() {
      this.bannerClosed = true;
    },
    saleEndDate() {
      const campaignData = CAMPAIGN_PROMOTIONS['jumpstart-2025'];
      const promoOptions = getPromotion(campaignData.promos, this.promotion);
      this.promotion = {
        buttonText: 'Start Now',
        ...promoOptions.nextPromotion,
        ...campaignData.templates,
      };
      if (promoOptions.refresh) {
        setTimeout(() => {
          window.$nuxt.refresh();
        }, 1000);
      }

      return new Date(this.promotion.endDate);
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes heroAnimation {
  0% {
    transform: rotateX(0deg);
  }

  50% {
    transform: rotateX(90deg);
  }

  100% {
    transform: rotateX(0deg);
  }
}

@include bp($bp-desktop) {
  .mobile-only,
  .laptop-only,
  ::v-deep .mobile-only,
  ::v-deep .laptop-only {
    display: none !important;
  }
}

.block {
  display: block;
}

.highlight,
::v-deep .highlight {
  font-weight: 700;

  &.campaign-1,
  &.blue {
    color: var(--c-js2025-1);

    &.special,
    &.special-ds {
      text-shadow: 0 0 12px rgba(37, 244, 238, 0.5);
    }
  }

  &.campaign-2,
  &.yellow {
    color: var(--c-js2025-2);
  }

  &.campaign-3,
  &.magenta {
    color: var(--c-js2025-3);
  }

  &.white {
    font-weight: 600;
    color: var(--main-light);

    &.special,
    &.special-ds {
      text-shadow: 0 0 12px rgba(37, 244, 238, 0.5);
    }
  }

  &.thin {
    font-weight: inherit;
  }
}

.fa {
  .highlight,
  ::v-deep .highlight {
    &.fa-campaign-1,
    &.fa-blue,
    &.campaign-1,
    &.blue {
      color: var(--c-js2025-1);

      &.special {
        text-shadow: 0 0 12px var(--accent);
      }

      &.special-ds {
        text-shadow: none;
      }
    }

    &.fa-campaign-2,
    &.fa-yellow {
      color: var(--c-js2025-2);
    }

    &.fa-campaign-3,
    &.fa-magenta {
      color: var(--c-js2025-3);
    }

    &.white {
      &.special {
        text-shadow: 0 0 12px var(--accent);
      }

      &.special-ds {
        text-shadow: none;
      }
    }
  }
}

::v-deep {
  .course-card.dark {
    .course-card-body {
      .course-info {
        .course-name {
          font-size: 18px;
          font-weight: 500;
        }

        .info {
          font-size: 16px;
          font-weight: 400;
        }
      }
    }

    .course-card-container {
      background-color: var(--main-dark-light-1);
    }
  }
}

.extend-label {
  margin-top: 30px;
  display: inline-block;
  font-size: 24px;
  font-weight: 700;
  padding: 12px;
  line-height: 18px;
  background-color: var(--c-lda-3);
  transform: rotate(-5deg);
}

.btn-primary,
::v-deep .btn-primary {
  color: var(--main-dark);
  border: 2px solid var(--c-lda-1);
  background-color: var(--c-lda-1);
  font-weight: 600;
  font-size: 21px;
  border-radius: 0;
  margin: 0 auto;
  display: block;
  height: 58px;
  line-height: 18px;
  max-width: 350px;
  width: 100%;
  margin-top: var(--spacing-60);
  padding-left: 5px;
  padding-right: 5px;

  &.submit {
    margin-top: 20px;
    margin-bottom: 12px;
    line-height: 30px;
    outline: 2px solid var(--main-dark) !important;
  }

  &:not(:disabled) {
    &:hover,
    &:active,
    &:focus {
      color: var(--main-dark);
      background-color: var(--c-lda-1);
      border-color: var(--c-lda-1);

      &:not(.submit) {
        box-shadow: 0px 0px 15px #FAFF00;
      }
    }
  }
}

::v-deep .limited-offer .btn-primary.submit,
::v-deep .bottom-banner .btn-primary.submit {
  background-color: var(--c-mw-2);

  &:not(:disabled) {
    &:hover,
    &:active,
    &:focus {
      color: var(--main-dark);
      background-color: var(--c-mw-2);
    }
  }
}

.landing-page-v2 {
  background-color: var(--main-dark);
  color: var(--main-light);
  min-height: 100vh;

  h1 {
    font-size: 64px;
    font-weight: 500;
    line-height: 1.1;
    margin-bottom: 40px;

    .code-block img {
      height: 35px;
      display: block;
      margin-bottom: var(--spacing-8);
    }
  }

  .section-title,
  ::v-deep .section-title {
    font-size: 40px;

    // .highlight {
    //   font-weight: inherit;
    // }

    .smaller {
      display: block;
      margin-top: 12px;
      font-size: 32px;
    }
  }

  .muted {
    color: var(--main-light-shade-4);
  }

  .subtitle {
    font-size: 18px;
    font-weight: 500;
  }

  .animation {
    font-family: "Source Code Pro", monospace;
    font-optical-sizing: auto;
    font-weight: 600;
    font-style: normal;
    display: inline-block;
    font-size: 18px;
    line-height: 18px;
    width: 180px;
    height: 23px;
    color: var(--main-dark);
    background-color: #F0F0F2;
    border: 1px solid #ABAFB5;
    text-align: center;
    transform-style: preserve-3d;

    &.animate {
      animation: heroAnimation 0.6s ease-in-out;
    }
  }

  .static-salary-container {
    width: 188px;
    display: inline-block;
  }

  .animation-salary {
    vertical-align: middle;
    display: inline-block;
    width: 188px;

    .holder {
      display: block;
      white-space: nowrap;
      overflow: hidden;
      width: 0;

      &.animation {
        text-align: left;
        line-height: 23px;
      }

      &.active {
        transition: width 0.5s;
        width: var(--width, 188px);
      }
    }

    ::v-deep {
      .letter {
        font-weight: normal;
        opacity: 0;
        transition: all 0.1s linear;

        &.show {
          opacity: 1;
        }

        &:first-child {
          padding-left: 4px;
        }

        &:last-child {
          padding-right: 4px;
        }

        &.bold {
          font-weight: 700;
        }
      }
    }
  }

  .section {
    background-size: cover;
    background-position: bottom;
    padding-top: 80px;
    padding-bottom: 80px;

    &.instructors {
      .section-title {
        text-align: center;
      }

      ::v-deep .infinite-head .h4 {
        text-align: center;
      }
    }
  }

  &.extend {
    .highlight.yellow.special {
      text-shadow: 4px 4px 0 var(--main-dark);
    }

    .btn-primary,
    ::v-deep .btn-primary {
      &.submit:not(:hover) {
        box-shadow: 6px 6px 0 0 var(--c-lda-3);
      }
    }

    ::v-deep .bottom-banner {
      .campaign-heading {
        .extend-label {
          transform: none;
          padding-top: 8px;
          padding-bottom: 8px;
          font-size: 16px;
          margin: 0;
        }

        .block {
          display: inline;
        }

        .highlight.blue {
          font-size: 52px;
        }
      }
    }
  }

  &.ds .hero {
    background-image: url('../assets/images/campaigns/ds-js2025-bg-hero.webp');
  }

  &.fa {
    .hero {
      background-image: url('../assets/images/campaigns/fa-js2025-bg-hero.webp');

      ::v-deep {
        .holder .card {
          width: 420px;
          border-color: transparent;
          box-shadow: 0 0 60px 0 var(--c-js2025-1);
        }

        .hero-benefits {
          .benefit svg [stroke] {
            stroke: var(--c-js2025-1);
          }
        }
      }
    }

    ::v-deep .limited-offer-holder .limited-offer .switch-holder .switch input:checked + .slider {
      background-color: #68A9FE;
    }
  }

  ::v-deep .counter-with-discount.filled.discount-extended {
    border: 2px solid var(--c-js2025-2);
    max-width: fit-content;
    background-color: var(--main-dark);

    .square {
      flex: 0 0 auto;
      width: 84px;
      border-radius: 0;
      margin-right: 0;
      padding-left: 16px;
      padding-right: 16px;

      &.discount {
        white-space: nowrap;
        width: auto;
        border-right: 2px solid var(--c-js2025-2);
        font-size: 40px;
        font-weight: 700;
        color: var(--c-js2025-2);
        text-shadow: 0 0 12px var(--c-js2025-2);

        .number {
          line-height: 40px;
        }

        .display-text {
          color: var(--main-light);
          font-weight: 600;
          text-shadow: none;
        }
      }

      &:not(.discount) {
        .display-text {
          color: var(--main-light-shade-2);
        }
      }
    }
  }

  .hero {
    min-height: 700px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-size: auto 100%;
    background-position: center;
    background-repeat: no-repeat;
    padding-top: 0;

    ::v-deep .trusted-companies {
      margin-top: var(--spacing-36);
      padding-top: var(--spacing-36);
      border-color: rgba(255, 255, 255, 0.24);

      .logos {
        flex-wrap: wrap;
        justify-content: center;
        row-gap: 32px;
        column-gap: 66px;

        .logo {
          svg {
            height: 24px;
            color: var(--main-light-shade-3);

            g[fill] {
              fill: var(--main-light-shade-3);
            }

            &.pwc {
              height: 28px;
            }
          }
        }
      }
    }

    ::v-deep .content {
      padding-right: 0;

      .title .main {
        font-size: 48px;
        font-weight: 500;
        margin-bottom: 12px;

        .highlight {
          font-weight: 900;
          white-space: nowrap;
        }
      }

      .text-2 {
        font-size: 24px;
        line-height: 1.1;
      }
    }

    ::v-deep .hero-benefits {
      font-size: 16px;
      line-height: 1.4;

      .benefit svg [stroke] {
        stroke: var(--c-js2025-1);
      }
    }

    ::v-deep .holder {
      display: flex;
      align-items: center;

      .card {
        padding: var(--spacing-36);
        background-color: var(--main-light);
        width: 420px;
        box-shadow: 0 0 60px 0 var(--c-js2025-1);
        position: relative;
        color: var(--main-dark);

        .label {
          color: var(--main-dark-light-4);
          font-size: 20px;
          font-weight: 600;
          margin: 0;
        }

        .old-price {
          font-size: 30px;
          line-height: 110%;
          font-weight: 500;
          margin-top: var(--spacing-12);
          margin-bottom: 0;
          position: relative;
          display: inline-block;

          .del:after {
            content: '';
            width: 106px;
            height: 2px;
            background: var(--main-dark-light-1);
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
        }

        .new-price {
          font-size: 72px;
          font-weight: 500;
          line-height: 1.1;
          margin-top: var(--spacing-4);
          margin-bottom: var(--spacing-4);
          display: flex;
          align-items: flex-end;
          justify-content: flex-start;

          .medium {
            font-size: 30px;
            line-height: 50px;
          }

          .small {
            font-size: 21px;
            font-weight: 400;
            line-height: 50px;
          }
        }

        .text {
          margin: 12px 0;
          font-size: 21px;
          line-height: 1.4;
        }

        .counter {
          &.filled .square {
            width: 22.5%;
            border-radius: 0;
            background-color: var(--main-dark-light-2);
            border: 1px solid var(--c-js2025-1);
          }
        }

        .card-text {
          font-size: 14px;
          line-height: 150%;
          color: var(--main-dark-light-2);
          text-align: center;
          margin: 0;
        }

        .stamp {
          position: absolute;
          top: 36px;
          right: 36px;

          &.not-image {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            border: 2px solid var(--main-dark);
            background-color: var(--c-js2025-3);
            display: flex;
            text-align: center;
            justify-content: center;
            align-items: center;
            color: var(--main-light);
            font-size: 22px;
            font-weight: 800;
          }

          img {
            display: block;
            width: 100px;
          }
        }
      }
    }

    ::v-deep {
      .counter-with-discount.filled {
        margin-bottom: 28px;
      }

      .footer-text {
        font-size: 18px;

        .upper {
          margin-top: 8px;
          font-size: 28px;
          font-weight: 500;

          &.highlight {
            font-size: 800;
          }
        }
      }
    }
  }

  .companies-footer {
    ::v-deep .trusted-companies {
      margin-top: 30px;
      padding-top: 0;
      border-color: transparent;

      .logos {
        .logo {
          svg {
            height: 30px;
            color: var(--main-light-shade-3);
          }
        }
      }
    }
  }

  .trustpilot-review {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    margin-top: 40px;

    svg {
      height: 40px;
      margin-right: 26px;

      ~ svg {
        height: 38px;
        margin-right: 0;
      }
    }

    .rating {
      font-size: 28px;
      font-weight: 500;
      color: var(--main-light-shade-4);
      margin-right: 20px;
    }
  }

  .technologies {
    padding-top: 40px;

    .technologies-title {
      text-align: center;
      font-size: 18px;
      font-weight: 500;
      margin-bottom: 28px;
    }

    .container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 40px;
      border-bottom: 1px solid var(--main-dark-light-4);

      .single-technology {
        display: flex;
        align-items: center;
        color: var(--main-light-shade-3);

        svg {
          height: 24px;
          margin-right: 10px;

          // [fill] {
          //   fill: var(--main-light-shade-3);
          // }

          // [stroke] {
          //   stroke: var(--main-light-shade-3);
          // }
        }
      }
    }
  }

  &.fa .technologies .container {
    border-bottom: 0;

    .single-technology svg {
      height: 32px;
    }
  }

  .instructors {
    ::v-deep {
      .trusted-companies {
        padding-top: 0;
        border-top: none;
        color: var(--main-light);

        .logos {
          justify-content: center;
          column-gap: 70px;
        }
      }

      .infinite-carousel .carousel .inner > * {
        width: 210px;
        margin-right: 30px;
        border: 4px solid var(--main-dark);
        border-radius: 0;

        img {
          display: block;
          width: 100%;
        }
      }
    }
  }

  .section-footer {
    border-top: 1px solid var(--main-dark-light-3);
    margin-top: 30px;
    padding-top: 32px;

    > * + * {
      margin-top: 40px;
    }

    ::v-deep .trusted-companies {
      margin-top: 0;
      padding-top: 0;
      border-top: none;

      .title {
        color: var(--main-light);
      }

      .logos {
        .logo {
          svg {
            height: 30px;

            &,
            g[fill] {
              color: var(--main-light-shade-4);
            }

            path[fill],
            g[fill] {
              fill: var(--main-light-shade-4);
            }
          }
        }
      }
    }

    .trustpilot-review {
      display: flex;
      align-items: flex-end;
      justify-content: center;

      svg {
        height: 40px;
        margin-right: 26px;

        ~ svg {
          height: 38px;
          margin-right: 0;
        }
      }

      .rating {
        font-size: 28px;
        font-weight: 500;
        color: var(--main-light-shade-4);
        margin-right: 20px;
      }
    }
  }

  .btn-holder {
    display: flex;
    justify-content: center;
    margin-top: 32px;
  }

  .stats {
    ::v-deep .stats-icons {
      margin-top: 0;

      > div {
        align-items: center;

        .description {
          font-size: 21px;
        }

        &:nth-child(2) {
          svg {
            color: var(--c-js2025-3);
          }
        }
      }

      svg {
        width: 90px;
        height: 90px;
      }
    }

    &.fa {
      ::v-deep .stats-icons > div:not(:nth-child(2)) {
        svg {
          color: #68A9FE;
        }
      }
    }
  }

  .courses-extended {
    .head {
      text-align: center;

      h3 {
        margin: 40px 0;
      }

      ul {
        list-style: none;
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 16px;
        padding-left: 0;

        li {
          padding: 20px;
          font-size: 18px;
          line-height: 1.1;
          color: var(--main-light);
          background-color: var(--main-dark-light-1);
          border: 2px solid transparent;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover,
          &.active {
            border-color: var(--c-js2025-1);
            background-color: var(--main-dark-light-2);
          }
        }
      }

      .description {
        font-size: 24px;
        font-weight: 500;
        margin-top: 60px;
        margin-bottom: 0;
      }
    }

    ::v-deep .courses {
      padding-bottom: 0;
    }

    .btn-holder {
      margin-top: 0;
    }

    &.fa .head ul li {
      &:hover,
      &.active {
        border-color: var(--c-js2025-3);
      }
    }
  }

  .promo-video {
    .video {
      padding-top: 0;
      padding-bottom: 0;

      ::v-deep {
        h2 {
          max-width: 100%;
        }

        .video-js {
          border-radius: 0;
          border: none;
          box-shadow: none;
        }
      }
    }

    ::v-deep .stats-icons .description {
      white-space: nowrap;
    }
  }

  .free {
    .section-title {
      text-align: center
    }

    .section-description {
      text-align: center;
      font-size: 21px;
      font-weight: 400;
      line-height: 1.4;
      margin-bottom: 40px;
    }

    .cards {
      row-gap: 30px;

      .card {
        .card-image {
          display: block;
          max-width: 100%;
          margin: auto;
        }

        .card-title {
          margin-top: 20px;
        }

        .card-desc {
          font-size: 18px;
          line-height: 1.4;
        }
      }
    }

    .btn-holder {
      margin-top: 0;
    }
  }

  .courses-extended {
    ::v-deep .infinite-carousel {
      .controls {
        margin-top: 0;
      }

      .carousel {
        padding-top: 10px;
        margin-top: -10px;
        padding-bottom: 10px;
        margin-bottom: -10px;

        .card {
          margin: 0 var(--spacing-30) 0 0;
          width: 370px;
          vertical-align: top;

          .course-card-container {
            min-height: 422px;
          }

          &:not(.course-card) {
            flex-direction: column;
            align-items: flex-start;
            height: auto;
            border-radius: 0;

            .image-wrapper img {
              display: block;
              max-width: 100%;
              max-height: 100%;
              border-radius: 7px;
            }

            .card-title {
              margin-top: var(--spacing-20);
              font-size: 32px;
              color: var(--main-light);
            }

            .card-desc {
              color: var(--main-dark-shade-4);
              font-size: 21px;
              line-height: 140%;
            }
          }

          &.course-card {
            .course-card-image {
              height: 220px;
            }

            .course-card-body {
              padding: var(--spacing-20) var(--spacing-24) 20px;

              .course-name {
                margin-bottom: var(--spacing-8);
              }

              .course-rating {
                color: var(--main-light);
              }

              .info {
                color: var(--main-light);
                margin: 0;

                .dot {
                  background: var(--main-light);
                }
              }
            }
          }
        }
      }
    }
  }

  .master {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;

    .muted {
      font-size: 21px;
    }

    img {
      display: block;
      max-width: 100%;
    }
  }

  .graduations {
    background-size: cover;
    background-position: bottom;

    ::v-deep {
      .title-highlight {
        font-size: 32px;

        svg {
          height: 38px;
        }
      }

      .graduates-thoughts .videos .single-video {
        .video-image {
          border-radius: 0;
        }

        .quote svg {
          margin-bottom: 4px;

          [fill] {
            fill: var(--c-js2025-3);
          }
        }
      }
   }

    .heading img {
      height: 44px;
      display: inline-block;
      vertical-align: bottom;
    }
  }

  .dream {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;
    text-align: center;

    .title-description {
      font-size: 32px;
      font-weight: 500;
      margin: 0;
    }

    .row {
      margin-top: 60px;
      margin-bottom: 18px;
    }

    .col {
      svg {
        color: var(--accent);
      }

      .title {
        font-size: 28px;
        font-weight: 500;
        color: var(--accent);
      }

      .description {
        font-size: 18px;
        font-weight: 400;
        line-height: 1.5;
      }
    }
  }

  .projects {
    ::v-deep {
      .home-small .card,
      .home-big .card {
        border-radius: 0;
      }

      .subtitle {
        margin-left: 0;
        width: 100%;
        max-width: 100%;
        flex: 1
      }
    }
  }

  .long-reviews-holder {
    ::v-deep {
      .title {
        text-align: center;
        margin-right: 0;
      }

      .reviews-trustpilot {
        .reviews-body .card-trustpilot-review {
          border-radius: 0;
        }

        .dark .see-more:after {
          bottom: 0;
          background: linear-gradient(2deg, #011633 2.01%, rgba(1, 22, 51, 0.77) 35.57%, rgba(1, 22, 51, 0.00) 86.79%);
        }
      }
    }
  }

  &.ds ::v-deep .limited-offer-holder {
    background-image: url('../assets/images/campaigns/ds-js2025-bg-footer.webp');
  }

  &.fa ::v-deep .limited-offer-holder {
    background-image: url('../assets/images/campaigns/fa-js2025-bg-footer.webp');
  }

  ::v-deep .limited-offer-holder {
    margin-top: -80px;
    padding-top: 230px;
    position: relative;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center top;

    .limited-offer {
      position: relative;
      z-index: 2;
      color: var(--main-light);

      h2 {
        margin-bottom: var(--spacing-40);

        .highlight {
          font-weight: 900;
        }
      }

      .subtitle {
        color: var(--c-js2025-2);
        font-size: 21px;
        line-height: 1.1;
        margin-bottom: 12px;
      }

      .heading {
        font-size: 40px;

        img {
          display: block;
          margin: 0 auto;
        }

        .text-2 {
          font-size: 44px;
          font-weight: 800;
        }

        .text-3 {
          font-size: 40px;
        }
      }

      .price {
        margin-top: var(--spacing-20);
        margin-bottom: var(--spacing-30);

        .regular-price {
          align-self: flex-start;
          margin-top: 10px;
          font-weight: 500;
          color: #E6E7E9;

          &:before {
            background-color: #E6E7E9;
          }
        }

        .discounted-price {
          line-height: 70px;
          font-weight: 700;
          font-size: 60px;

          .body-1 {
            font-weight: 400;
            margin-left: -10px;
          }
        }
      }

      .plan-features {
        margin-bottom: var(--spacing-50);

        .feature {
          position: relative;
          line-height: 150%;
          margin-bottom: 8px;

          svg {
            [fill] {
              fill: var(--c-js2025-1);
            }

            [stroke] {
              stroke: var(--c-js2025-1);
            }
          }
        }
      }

      hr {
        display: none;
      }

      .switch-holder {
        span:not(.unactive) {
          color: var(--main-light);
        }

        .switch {
          width: 45.69px;
          height: 22px;

          .slider:before {
            width: 16px;
            height: 16px;
            top: 3px;
            left: 3px;
          }

          // input:not(:checked) + .slider {
          //   background-color: var(--c-tyf-1);
          // }

          input:checked + .slider {
            background-color: var(--c-js2025-3);

            &:before {
              transform: translateX(23px);
            }
          }
        }
      }

      .btn-primary {
        margin-top: 0;
      }

      .badges {
        margin-top: 40px;
      }

      .vat-note {
        margin-top: var(--spacing-24);
        color: var(--main-light);
        font-weight: 400;
        font-size: 18px;
      }
    }

    .limited-offer-background {
      position: absolute;
      top: 0;
      left: 50%;
      bottom: 0;
      width: 100%;
      height: calc(100% + 50px);
      object-fit: cover;
      transform: translateX(-50%);
    }
  }

  ::v-deep .bottom-banner {
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    height: 110px;
    padding: 0;

    &.ds {
      background-image: url('../assets/images/campaigns/ds-js2025-bg-bottom-banner.webp');
    }

    &.fa {
      background-image: url('../assets/images/campaigns/fa-js2025-bg-bottom-banner.webp');
    }

    &:before {
      content: '';
      position: absolute;
      display: block;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, var(--c-js2025-1) 0%, var(--c-js2025-3) 100%);
    }

    &.final-promo {
      background-image: url('../assets/images/campaigns/ds-js2025-bg-footer.webp');
    }

    .container {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }

    .campaign-heading {
      display: flex;
      align-items: center;
      font-size: 20px;
      font-weight: 400;

      .block {
        display: block;
      }

      .highlight {
        &.white {
          font-size: 40px;
          font-weight: 900;
        }

        &.blue {
          font-size: 28px;
          font-weight: 800;
        }
      }
    }

    .counter.filled .square {
      border-radius: 0;
      border: 2px solid var(--c-js2025-3);
    }

    .btn-primary {
      width: 220px;
      height: 58px;
      margin: 0;
      line-height: 20px;
      border: 0;
    }

    .icon-close {
      top: 19px;
      right: 14px;

      path[fill] {
        fill: var(--main-light-shade-2);
      }
    }
  }

  &.fa ::v-deep .bottom-banner {
    border-color: var(--c-js2025-3);
  }

  @include bp($bp-laptop) {
    .section-title,
    ::v-deep .section-title {
      font-size: 32px;

      .smaller {
        font-size: 28px;
      }
    }

    .extend-label {
      margin-top: 10px;
      font-size: 21px;
      line-height: 12px;
      margin-bottom: 10px;
    }

    .hero {
      .content {
        .title .main {
          font-size: 40px;
        }
      }

      .footer-text .upper {
        font-size: 24px;
      }
    }

    .instructors {
      ::v-deep {
        .infinite-carousel .carousel .inner > * {
          width: 178px;
        }
      }
    }

    .section-footer ::v-deep {
      .trusted-companies {
        .logos {
          .logo svg {
            height: 22px;
          }
        }
      }
    }

    .courses-extended {
      .head {
        ul li {
          font-size: 14px;
        }

        .description {
          font-size: 21px;
        }
      }

      ::v-deep {
        .infinite-carousel .carousel .course-card {
          width: 310px;
        }
      }
    }

    .stats ::v-deep .stats-icons {
      svg {
        width: 60px;
        height: 60px;
      }

      > div .description {
        font-size: 18px;
      }
    }

    .dream {
      .title-description {
        font-size: 28px;
      }

      .col {
        .title {
          font-size: 24px;
        }
      }
    }

    .landing-footer {
      .title-description {
        font-size: 28px;
      }

      .title-description-2 {
        font-size: 32px;
      }
    }

    ::v-deep .limited-offer-holder {
      .limited-offer {
        .limited-offer-container {
          width: 700px;
          max-width: 700px;
        }

        .subtitle {
          font-size: 21px;
        }

        .heading {
          &,
          .text-2,
          .text-3 {
            font-size: 32px;
          }

          img {
            height: 60px;
          }
        }

        .subtitle .title-inline {
          width: 240px;
        }

        .price {
          margin-bottom: 46px;

          .discounted-price {
            font-size: 54px;
          }
        }

        .btn-primary {
          max-width: 310px;
        }
      }

      .limited-offer-background {
        bottom: -160px;
      }
    }

    ::v-deep .bottom-banner {
      &.ds {
        background-image: url('../assets/images/campaigns/ds-js2025-bg-bottom-banner-laptop.webp');
      }

      &.fa {
        background-image: url('../assets/images/campaigns/fa-js2025-bg-bottom-banner-laptop.webp');
      }

      .campaign-heading {
        font-size: 18px;

        .highlight {
          &.white {
            font-size: 36px;
          }

          &.blue {
            font-size: 24px;
          }
        }
      }

      .btn-primary {
        width: 180px;
        font-size: 18px;
      }
    }

    &.extend {
      ::v-deep .bottom-banner {
        .campaign-heading {
          .extend-label {
            font-size: 14px;
          }

          .highlight {
            font-size: 36px;

            &.blue {
              font-size: 48px;

              .smaller {
                font-size: 32px;
              }
            }
          }
        }

        .counter {
          .square:not(:last-of-type) {
            margin-right: 8px;
          }
        }
      }
    }
  }

  @include bp($bp-mobile) {
    .desktop-only,
    ::v-deep .desktop-only {
      display: none !important;
    }

    .mobile-block,
    ::v-deep .mobile-block {
      display: block;
    }

    .static-salary-container,
    .animation-salary {
      width: auto;
    }

    .extend-label {
      margin: 0 auto 20px;
      font-size: 21px;
      line-height: 12px;
    }

    .btn-holder {
      margin-top: 0;
    }

    ::v-deep .counter-with-discount.filled.discount-extended {
      .square {
        padding-left: 12px;
        padding-right: 12px;
      }
    }

    .btn-primary.submit,
    ::v-deep .btn-primary.submit {
      font-size: 18px;
      line-height: 14px;
    }

    .section {
      padding-top: 60px;
      padding-bottom: 60px;
      background-size: contain;
      background-position: bottom;
      background-repeat: no-repeat;
    }

    :not(.hero) .btn-primary {
      width: 100%;
      max-width: 327px;
    }

    .subtitle {
      font-size: 20px;
    }

    .section-title,
    ::v-deep .section-title {
      font-size: 32px;

      .smaller {
        font-size: 28px;
      }
    }

    &.ds .hero {
      background-image: url('../assets/images/campaigns/ds-js2025-bg-hero-mobile.webp');
    }

    &.fa .hero {
      background-image: url('../assets/images/campaigns/fa-js2025-bg-hero-mobile.webp');
    }

    .hero {
      padding-top: 108px;
      padding-bottom: 60px;

      .content {
        .title .main {
          text-align: center;
          font-size: 40px;

          .highlight {
            &.yellow {
              font-size: 77px;
            }

            &.blue {
              font-size: 42px;
              letter-spacing: normal;
            }
          }

          .block {
            display: block;
            font-size: 28px;
          }
        }

        .text-2 {
          font-size: 21px;
        }
      }

      ::v-deep .trusted-companies {
        margin-top: 40px;
        padding-top: 40px;
        margin-bottom: 60px;

        .logos {
          column-gap: 40px;

          .logo svg {
            height: 20px;
          }
        }
      }

      ::v-deep .holder {
        padding: 0;
        margin-top: 0;

        .card {
          padding: 24px;
          margin-right: auto;
          margin-bottom: 0;

          .stamp {
            top: 24px;
            right: 24px;

            img {
              width: 80px;
            }
          }

          .card-text {
            font-size: 12px;
          }

          .old-price {
            font-size: 24px;
          }

          .discounted-price {
            font-size: 59px;
          }

          .text {
            font-size: 18px;
          }
        }

        .hero-benefits .benefit {
          font-size: 16px;
        }

        .footer-text {
          display: block;
          text-align: center;
          margin-top: 40px;

          .upper {
            font-size: 24px;
          }
        }
      }

      .mobile-only {
        margin-top: 40px;

        .subtitle {
          font-weight: 400;
        }
      }
    }

    .companies-footer {
      ::v-deep {
        .trusted-companies {
          margin-bottom: 30px;

          .title {
            margin-top: 0;
            margin-bottom: 30px;
          }

          .logos .logo svg {
            height: 24px;
          }
        }

        .trustpilot-review {
          margin-top: 0;
          align-items: center;

          > svg {
            height: 26px;
            flex: 0 0 auto;
          }

          .rating {
            font-size: 20px;
            line-height: 26px;
          }

          .course-rating {
            font-size: 20px;

            .rating-stars svg {
              width: 25px;
              height: 25px;
            }
          }
        }
      }
    }

    .instructors ::v-deep .infinite-carousel .carousel .inner > * {
      width: 327px;
    }

    > .technologies .container {
      width: auto;
      padding: 0 0 36px;
      margin: 0 24px;
      flex-wrap: wrap;
      column-gap: 25px;
      row-gap: 50px;
      justify-content: space-evenly;
    }

    &.fa .technologies .container {
      border-bottom: 1px solid var(--main-dark-light-4);

      .single-technology svg {
        height: 27px;
      }
    }

    .section-footer {
      padding-left: 0;
      padding-right: 0;
      margin-left: 24px;
      margin-right: 24px;
      width: auto;

      ::v-deep {
        .trusted-companies {
          margin-bottom: 30px;

          .title {
            margin-top: 0;
            margin-bottom: 30px;
          }

          .logos {
            .logo svg {
              height: 28px;
              margin-right: 0;
            }
          }
        }

        .trustpilot-review {
          margin-top: 0;
          justify-content: space-between;
          max-width: 375px;

          > svg {
            height: 26px;
            margin-right: auto;
            flex: 0 0 auto;

            ~ svg {
              height: 26px;
            }
          }

          .rating {
            font-size: 20px;
            margin-right: auto;
          }
        }
      }

      .btn-primary {
        margin-top: 32px;
      }
    }

    ::v-deep .trusted-companies .logos .logo svg {
      height: 23px;
    }

    ::v-deep .hero-benefits .benefit {
      font-size: 18px;
    }

    .courses-extended {
      .head {
        ul {
          gap: 12px;
          justify-content: flex-start;
        }

        .description {
          margin-top: 32px;

          .highlight {
            display: block;
          }
        }
      }

      .courses {
        padding-top: 32px;
        padding-bottom: 0;
      }

      .btn-holder {
        margin-top: 0;
      }

      ::v-deep .infinite-carousel .carousel .card {
        width: 327px;
      }
    }

    .stats ::v-deep .stats-icons {
      svg {
        width: 50px;
        height: 50px;
      }
      > div .description {
        font-size: 18px;
        white-space: nowrap;
      }
    }

    .master {
      text-align: center;
    }

    // .graduations {
    //   ::v-deep {
    //     .col {
    //       padding-left: 26px;
    //       padding-right: 26px;
    //     }
    //   }
    // }

    .dream {
      h2 {
        font-size: 32px;
      }

      .title-description {
        font-size: 28px;
      }
    }

    &.ds ::v-deep .limited-offer-holder {
      background-image: url('../assets/images/campaigns/ds-js2025-bg-footer-mobile.webp');
    }

    &.fa ::v-deep .limited-offer-holder {
      background-image: url('../assets/images/campaigns/fa-js2025-bg-footer-mobile.webp');
    }

    ::v-deep .limited-offer-holder {
      margin-top: -60px;
      padding-top: 160px;
      padding-bottom: 60px;
      background-size: auto 100%;
      background-position: top;

      .limited-offer {
        .container {
          h2 {
            margin: 0 auto var(--spacing-40);
          }
        }

        .plan-features {
          text-align: left;
          display: block;
          width: 100%;

          .feature {
            font-size: initial;
          }
        }

        .price {
          align-items: flex-start;

          .regular-price {
            font-size: 24px;
            margin-right: 8px;
          }

          .discounted-price {
            font-size: 50px;
          }
        }

        .subtitle {
          font-size: 21px;
          line-height: 110%;
          display: block;
          text-align: center;

          .title-inline {
            display: block;
            width: 258px;
            margin: 10px 0;
          }
        }

        .heading {
          font-size: 32px;

          .text-2 {
            font-size: 32px;
            margin: 12px 0;
          }

          .text-3 {
            font-size: 32px;
          }
        }
      }

      .limited-offer-background {
        top: 0;
        bottom: 0;
        width: 100%;
        object-fit: cover;
        object-position: top;
        height: 100%;
      }
    }
  }
}
</style>
